import { useState, useCallback, useEffect } from "react";
import { utcIsoToIstDate } from "../utils/date";
import {
  searchProfilesByPhone,
  addProfile,
  createWebsiteUser,
  UserNotFoundError,
  listAdminAcademies,
  listAdminCenters,
  listAgeCategoriesByCenter,
  listAdminBatches,
  fetchPlanFields,
  addOfflineEnrolment,
  updateOfflineEnrolment,
  type SearchProfilesData,
  type PlayerProfile,
  type AcademyListItem,
  type CenterListItem,
  type BatchListItem,
  type BatchFee,
  type BatchDynamicSlot,
  type CreateOfflineEnrolmentPayload,
  type UpdateOfflineEnrolmentPayload,
  type AddProfilePayload,
  type EnrolmentDetails,
  type BatchDetails,
  type PlanField,
} from "../../../../api/academyEnrolments/enrolmentsService";
import { sortAvailablePaymentFrequencies } from "../planFieldsOrdering";

export type OfflinePlayerMode = "phone" | "direct";

export type RegisterBy = "self" | "parent";

export interface SelectedSlot {
  days?: string[];
  day?: string;
  startTime: string;
  endTime: string;
}

export interface DirectPlayer {
  fullName: string;
  gender: string;
  dob: string;
}

export interface DirectPlayerCreated {
  user: string;
  player: PlayerProfile;
}

export interface OfflineEnrolmentFormState {
  // Player section
  playerMode: OfflinePlayerMode;

  // Phone sub-mode
  phoneNumber: string;
  searchedProfileData: SearchProfilesData | null;
  phoneSearched: boolean;
  isNewUser: boolean;
  phoneSearchError: string | null;
  showAddProfileForm: boolean;
  registerBy: RegisterBy | "";

  // New-profile form fields (phone mode)
  newParentName: string;
  newParentGender: string;
  newParentDob: string;
  newPlayerName: string;
  newPlayerGender: string;
  newPlayerDob: string;

  selectedPlayer: PlayerProfile | null;

  // Direct sub-mode
  directPlayer: DirectPlayer;
  directPlayerCreated: DirectPlayerCreated | null;
  directPlayerError: string | null;

  // Academy + Sport
  academyList: AcademyListItem[];
  selectedAcademy: AcademyListItem | null;
  selectedSport: string;

  // Center
  centerList: CenterListItem[];
  selectedCenter: CenterListItem | null;

  // Age category
  ageCategoryList: string[];
  selectedAgeCategory: string;

  // Batch
  batchList: BatchListItem[];
  selectedBatch: BatchListItem | null;

  // Slot
  selectedSlot: SelectedSlot | null;
  selectedDynamicSlots: SelectedSlot[];

  // Frequencies
  selectedFee: BatchFee | null;
  selectedTrainingFrequency: string;
  selectedPaymentFrequency: string;

  // Schedule & payment
  startDate: string;
  endDate: string;
  paidAt: string;
  paymentAmount: string;
}

export interface OfflineEnrolmentFormLoading {
  phoneSearch: boolean;
  addProfile: boolean;
  createDirectPlayer: boolean;
  createUser: boolean;
  academies: boolean;
  centers: boolean;
  ageCategories: boolean;
  batches: boolean;
  submit: boolean;
}

function buildInitialState(): OfflineEnrolmentFormState {
  return {
    playerMode: "phone",
    phoneNumber: "",
    searchedProfileData: null,
    phoneSearched: false,
    isNewUser: false,
    phoneSearchError: null,
    showAddProfileForm: false,
    registerBy: "",
    newParentName: "",
    newParentGender: "",
    newParentDob: "",
    newPlayerName: "",
    newPlayerGender: "",
    newPlayerDob: "",
    selectedPlayer: null,
    directPlayer: { fullName: "", gender: "", dob: "" },
    directPlayerCreated: null,
    directPlayerError: null,
    academyList: [],
    selectedAcademy: null,
    selectedSport: "",
    centerList: [],
    selectedCenter: null,
    ageCategoryList: [],
    selectedAgeCategory: "",
    batchList: [],
    selectedBatch: null,
    selectedSlot: null,
    selectedDynamicSlots: [],
    selectedFee: null,
    selectedTrainingFrequency: "",
    selectedPaymentFrequency: "",
    startDate: "",
    endDate: "",
    paidAt: "",
    paymentAmount: "",
  };
}

function buildInitialLoading(): OfflineEnrolmentFormLoading {
  return {
    phoneSearch: false,
    addProfile: false,
    createDirectPlayer: false,
    createUser: false,
    academies: false,
    centers: false,
    ageCategories: false,
    batches: false,
    submit: false,
  };
}

export type OfflineEnrolmentFormMode = "add" | "edit" | "view";

interface UseOfflineEnrolmentFormOptions {
  mode?: OfflineEnrolmentFormMode;
  initialEnrolment?: EnrolmentDetails | null;
  initialBatch?: BatchDetails | null;
  onSuccess?: () => void;
}

export function useOfflineEnrolmentForm({
  mode = "add",
  initialEnrolment,
  initialBatch,
  onSuccess,
}: UseOfflineEnrolmentFormOptions) {
  const [state, setState] = useState<OfflineEnrolmentFormState>(buildInitialState);
  const [loading, setLoading] = useState<OfflineEnrolmentFormLoading>(buildInitialLoading);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [planFields, setPlanFields] = useState<PlanField[]>([]);

  // True in edit mode when the enrolment has no attached player yet — allows the admin to attach one
  const allowPlayerAttach = mode === "edit" && !initialEnrolment?.player?._id;

  // ── Eager-load academies on mount ─────────────────────────────────────────
  useEffect(() => {
    setLoading((l) => ({ ...l, academies: true }));
    listAdminAcademies()
      .then((list) => setState((prev) => ({ ...prev, academyList: list })))
      .catch(() => setState((prev) => ({ ...prev, academyList: [] })))
      .finally(() => setLoading((l) => ({ ...l, academies: false })));
  }, []);

  useEffect(() => {
    fetchPlanFields()
      .then(setPlanFields)
      .catch(() => setPlanFields([]));
  }, []);

  // ── Hydrate for edit / view mode ──────────────────────────────────────────
  useEffect(() => {
    if ((mode !== "edit" && mode !== "view") || !initialEnrolment) return;

    const enrolment = initialEnrolment;
    const batch = initialBatch ?? null;

    const existingFixedSlot =
      enrolment.fixedSlots?.length > 0
        ? {
            days: enrolment.fixedSlots[0].days,
            startTime: enrolment.fixedSlots[0].startTime,
            endTime: enrolment.fixedSlots[0].endTime,
          }
        : null;

    const existingDynamicSlots: SelectedSlot[] =
      (enrolment.slots as BatchDynamicSlot[])?.length > 0
        ? (enrolment.slots as BatchDynamicSlot[]).map((s) => ({
            day: s.day,
            startTime: s.startTime,
            endTime: s.endTime,
          }))
        : [];

    const matchingFee =
      batch?.fees.find((f) => f.trainingFrequency === enrolment.trainingFrequency) ??
      batch?.fees[0] ??
      null;

    setState((prev) => ({
      ...prev,
      playerMode: "phone",
      // Branch: when the enrolment has no player yet, start the player section clean so the
      // admin can search and attach one; otherwise restore the existing player from the enrolment.
      ...(enrolment.player?._id
        ? {
            phoneNumber: String(enrolment.user?.phoneNumber ?? ""),
            phoneSearched: true,
            isNewUser: false,
            phoneSearchError: null,
            showAddProfileForm: false,
            searchedProfileData: {
              email: null,
              fullName: enrolment.player.fullName ?? "",
              location: enrolment.coachingCenter?.basicDetails?.location ?? "",
              phoneNumber: enrolment.user?.phoneNumber ?? 0,
              players: [
                {
                  _id: enrolment.player._id,
                  dob: null,
                  email: null,
                  fullName: enrolment.player.fullName ?? "",
                  gender: null,
                  profilePic: enrolment.player?.profilePic ?? null,
                  relationshipType: enrolment.enrolledBy === "self" ? "self" : "parent",
                },
              ],
              user: enrolment.user?._id ?? "",
            },
            registerBy: (enrolment.enrolledBy as RegisterBy) ?? "",
            selectedPlayer: {
              _id: enrolment.player._id,
              dob: null,
              email: null,
              fullName: enrolment.player.fullName ?? "",
              gender: null,
              profilePic: enrolment.player?.profilePic ?? null,
              relationshipType: enrolment.enrolledBy === "self" ? "self" : "parent",
            },
          }
        : {
            phoneNumber: "",
            phoneSearched: false,
            isNewUser: false,
            phoneSearchError: null,
            showAddProfileForm: false,
            searchedProfileData: null,
            registerBy: "" as RegisterBy | "",
            selectedPlayer: null,
          }),
      selectedAcademy: enrolment.academy
        ? {
            _id: enrolment.academy._id,
            basicDetails: {
              academyName: enrolment.academy.basicDetails.academyName,
              sports: [enrolment.sports],
            },
          }
        : null,
      selectedSport: enrolment.sports ?? "",
      selectedCenter: enrolment.coachingCenter
        ? {
            _id: enrolment.coachingCenter._id,
            basicDetails: {
              address: enrolment.coachingCenter.basicDetails.address,
              location: enrolment.coachingCenter.basicDetails.location,
            },
          }
        : null,
      ageCategoryList: enrolment.ageCategory ? [enrolment.ageCategory] : [],
      selectedAgeCategory: enrolment.ageCategory ?? batch?.ageCategory ?? "",
      selectedBatch: batch
        ? {
            _id: batch._id,
            ageCategory: batch.ageCategory,
            batchName: batch.batchName,
            course: batch.course,
            fees: batch.fees,
            fixedSlots: batch.fixedSlots,
            slotType: batch.slotType,
            slots: batch.slots,
            trainingDays: batch.trainingDays,
          }
        : null,
      selectedSlot: enrolment.slotType === "fixed" ? existingFixedSlot : null,
      selectedDynamicSlots: enrolment.slotType === "dynamic" ? existingDynamicSlots : [],
      selectedFee: matchingFee,
      selectedTrainingFrequency: enrolment.trainingFrequency ?? "",
      selectedPaymentFrequency: enrolment.paymentFrequency ?? "",
      startDate: utcIsoToIstDate(enrolment.startDate),
      endDate: utcIsoToIstDate(enrolment.endDate),
      paidAt: utcIsoToIstDate(enrolment.paidAt),
      paymentAmount: enrolment.totalFeeInfo?.totalFeeAfterGST != null ? String(enrolment.totalFeeInfo.totalFeeAfterGST) : "",
    }));
  }, [mode, initialEnrolment, initialBatch]);

  // ── Player mode switch ────────────────────────────────────────────────────
  const handlePlayerModeChange = useCallback((mode: OfflinePlayerMode) => {
    setState((prev) => ({
      ...prev,
      playerMode: mode,
      phoneNumber: "",
      searchedProfileData: null,
      phoneSearched: false,
      isNewUser: false,
      phoneSearchError: null,
      showAddProfileForm: false,
      registerBy: "",
      selectedPlayer: null,
      directPlayer: { fullName: "", gender: "", dob: "" },
      directPlayerCreated: null,
      directPlayerError: null,
    }));
  }, []);

  // ── Phone Search ──────────────────────────────────────────────────────────
  const handlePhoneSearch = useCallback(async () => {
    const cleaned = state.phoneNumber.trim().replace(/\D/g, "");
    if (!cleaned) return;

    setLoading((l) => ({ ...l, phoneSearch: true }));
    setSubmitError(null);

    try {
      const data = await searchProfilesByPhone(Number(cleaned));
      setState((prev) => ({
        ...prev,
        searchedProfileData: data,
        phoneSearched: true,
        isNewUser: false,
        phoneSearchError: null,
        showAddProfileForm: false,
        // In add mode (or when attaching a player in edit mode) reset registerBy on new search
        ...((mode === "add" || allowPlayerAttach) && { registerBy: "" as const }),
        // Keep selectedPlayer — new results appear as additional options
      }));
    } catch (err: unknown) {
      if (err instanceof UserNotFoundError) {
        setState((prev) => ({
          ...prev,
          ...((mode === "add" || allowPlayerAttach) && { searchedProfileData: null }),
          phoneSearched: true,
          isNewUser: true,
          phoneSearchError: err.apiMessage,
          showAddProfileForm: false,
          ...((mode === "add" || allowPlayerAttach) && { registerBy: "" as const }),
        }));
      } else {
        setSubmitError("Failed to search profiles. Please try again.");
      }
    } finally {
      setLoading((l) => ({ ...l, phoneSearch: false }));
    }
  }, [state.phoneNumber]);

  // ── Create website user (edit mode — phone not found) ─────────────────────
  const handleCreateWebsiteUser = useCallback(async () => {
    const cleaned = state.phoneNumber.trim().replace(/\D/g, "");
    if (!cleaned) return;

    setLoading((l) => ({ ...l, createUser: true }));
    setSubmitError(null);

    try {
      const created = await createWebsiteUser({ countryCode: 91, phoneNumber: Number(cleaned) });
      setState((prev) => ({
        ...prev,
        searchedProfileData: prev.searchedProfileData
          ? { ...prev.searchedProfileData, user: created._id, phoneNumber: created.phoneNumber }
          : {
              email: null,
              fullName: "",
              location: "",
              phoneNumber: created.phoneNumber,
              players: prev.selectedPlayer ? [prev.selectedPlayer] : [],
              user: created._id,
            },
        phoneSearched: true,
        isNewUser: false,
        phoneSearchError: null,
      }));
    } catch (createErr: unknown) {
      const anyErr = createErr as any;
      const body = anyErr?.response?.data;
      const msg = body?.error || body?.message || "Failed to create user. Please try again.";
      setSubmitError(msg);
    } finally {
      setLoading((l) => ({ ...l, createUser: false }));
    }
  }, [state.phoneNumber, state.selectedPlayer]);

  // ── Add new profile (phone mode) ──────────────────────────────────────────
  const handleAddProfile = useCallback(
    async (registerBy: RegisterBy) => {
      const cleaned = state.phoneNumber.trim().replace(/\D/g, "");
      if (!cleaned) return;

      setLoading((l) => ({ ...l, addProfile: true }));
      setSubmitError(null);

      try {
        const payload: AddProfilePayload = {
          phoneNumber: Number(cleaned),
          parent: {
            fullName: state.newParentName,
            gender: state.newParentGender,
            dob: state.newParentDob,
          },
        };
        if (registerBy === "parent") {
          payload.player = {
            fullName: state.newPlayerName,
            gender: state.newPlayerGender,
            dob: state.newPlayerDob,
          };
        }

        const data = await addProfile(payload);

        const profileList =
          data.isExistingUser
            ? (data.players ?? data.addedProfiles ?? [])
            : (data.createdProfiles ?? []);

        if (!profileList || profileList.length === 0) {
          setSubmitError("Profile created but no profile data was returned.");
          return;
        }

        const syntheticProfileData: SearchProfilesData = {
          email: data.email ?? null,
          fullName: data.fullName ?? profileList[0]?.fullName ?? "",
          location: data.location ?? "",
          phoneNumber: data.phoneNumber,
          players: profileList.map((p) => ({
            _id: p._id,
            dob: p.dob,
            email: null,
            fullName: p.fullName,
            gender: p.gender,
            profilePic: p.profilePic,
            relationshipType: p.relationshipType,
          })),
          user: data.user,
        };

        setState((prev) => ({
          ...prev,
          searchedProfileData: syntheticProfileData,
          isNewUser: false,
          phoneSearchError: null,
          showAddProfileForm: false,
          registerBy: prev.registerBy || registerBy,
          newParentName: "",
          newParentGender: "",
          newParentDob: "",
          newPlayerName: "",
          newPlayerGender: "",
          newPlayerDob: "",
          selectedPlayer: null,
        }));
      } catch (err: unknown) {
        const anyErr = err as any;
        const body = anyErr?.response?.data;
        const msg =
          body?.error ||
          body?.message ||
          (typeof err === "string" ? err : null) ||
          "Failed to create profile. Please try again.";
        setSubmitError(msg);
      } finally {
        setLoading((l) => ({ ...l, addProfile: false }));
      }
    },
    [state]
  );

  // ── Create direct player (direct mode) ────────────────────────────────────
  const handleCreateDirectPlayer = useCallback(async () => {
    const fullName = state.directPlayer.fullName.trim();
    if (!fullName) {
      setState((p) => ({ ...p, directPlayerError: "Player name is required." }));
      return;
    }

    setLoading((l) => ({ ...l, createDirectPlayer: true }));
    setState((p) => ({ ...p, directPlayerError: null }));

    try {
      const payload: AddProfilePayload = {
        player: {
          fullName,
          gender: state.directPlayer.gender || "",
          dob: state.directPlayer.dob || "",
        },
      };

      const data = await addProfile(payload);

      const profileList =
        data.isExistingUser
          ? (data.players ?? data.addedProfiles ?? [])
          : (data.createdProfiles ?? []);

      if (!profileList || profileList.length === 0) {
        setState((p) => ({
          ...p,
          directPlayerError: "Profile created but no profile data was returned.",
        }));
        return;
      }

      const created = profileList[0];
      const playerProfile: PlayerProfile = {
        _id: created._id,
        dob: created.dob,
        email: null,
        fullName: created.fullName,
        gender: created.gender,
        profilePic: created.profilePic,
        relationshipType: created.relationshipType,
      };

      setState((prev) => ({
        ...prev,
        directPlayerCreated: { user: data.user, player: playerProfile },
        directPlayerError: null,
      }));
    } catch (err: unknown) {
      const anyErr = err as any;
      const body = anyErr?.response?.data;
      const msg =
        body?.error ||
        body?.message ||
        (typeof err === "string" ? err : null) ||
        "Failed to create player profile. Please try again.";
      setState((p) => ({ ...p, directPlayerError: msg }));
    } finally {
      setLoading((l) => ({ ...l, createDirectPlayer: false }));
    }
  }, [state.directPlayer]);

  // ── Reset direct player ───────────────────────────────────────────────────
  const handleResetDirectPlayer = useCallback(() => {
    setState((prev) => ({
      ...prev,
      directPlayer: { fullName: "", gender: "", dob: "" },
      directPlayerCreated: null,
      directPlayerError: null,
    }));
  }, []);

  // ── Register-by select (phone mode) ──────────────────────────────────────
  const handleRegisterByChange = useCallback((value: RegisterBy) => {
    setState((prev) => ({ ...prev, registerBy: value, selectedPlayer: null }));
  }, []);

  // ── Player select (phone mode) ────────────────────────────────────────────
  const handlePlayerSelect = useCallback((player: PlayerProfile) => {
    setState((prev) => ({ ...prev, selectedPlayer: player }));
  }, []);

  // ── Direct player field setters ───────────────────────────────────────────
  const setDirectPlayerField = useCallback(
    (field: keyof DirectPlayer, value: string) => {
      setState((prev) => ({
        ...prev,
        directPlayer: { ...prev.directPlayer, [field]: value },
        // Changing fields clears the previously created profile
        directPlayerCreated: null,
        directPlayerError: null,
      }));
    },
    []
  );

  // ── Phone number setter ───────────────────────────────────────────────────
  const setPhoneNumber = (v: string) =>
    setState((p) => ({
      ...p,
      phoneNumber: v,
      phoneSearched: false,
      isNewUser: false,
      phoneSearchError: null,
      showAddProfileForm: false,
    // In add mode (or when attaching a player in edit mode) clear search state on number change
    ...((mode === "add" || allowPlayerAttach) && {
      searchedProfileData: null,
      registerBy: "" as const,
      selectedPlayer: null,
    }),
    }));

  const setNewParentName = (v: string) => setState((p) => ({ ...p, newParentName: v }));
  const setNewParentGender = (v: string) => setState((p) => ({ ...p, newParentGender: v }));
  const setNewParentDob = (v: string) => setState((p) => ({ ...p, newParentDob: v }));
  const setNewPlayerName = (v: string) => setState((p) => ({ ...p, newPlayerName: v }));
  const setNewPlayerGender = (v: string) => setState((p) => ({ ...p, newPlayerGender: v }));
  const setNewPlayerDob = (v: string) => setState((p) => ({ ...p, newPlayerDob: v }));
  const setShowAddProfileForm = (v: boolean) =>
    setState((p) => ({ ...p, showAddProfileForm: v }));

  // ── Academy select ────────────────────────────────────────────────────────
  const handleAcademySelect = useCallback((academy: AcademyListItem) => {
    const autoSport =
      academy.basicDetails.sports.length === 1 ? academy.basicDetails.sports[0] : "";
    setState((prev) => ({
      ...prev,
      selectedAcademy: academy,
      selectedSport: autoSport,
      centerList: [],
      selectedCenter: null,
      ageCategoryList: [],
      selectedAgeCategory: "",
      batchList: [],
      selectedBatch: null,
      selectedSlot: null,
      selectedDynamicSlots: [],
      selectedFee: null,
      selectedTrainingFrequency: "",
      selectedPaymentFrequency: "",
    }));
  }, []);

  const handleSportSelect = useCallback((sport: string) => {
    setState((prev) => ({ ...prev, selectedSport: sport }));
  }, []);

  // ── Load centers ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!state.selectedAcademy) return;
    setLoading((l) => ({ ...l, centers: true }));
    listAdminCenters(state.selectedAcademy._id)
      .then((list) => setState((prev) => ({ ...prev, centerList: list })))
      .catch(() => setState((prev) => ({ ...prev, centerList: [] })))
      .finally(() => setLoading((l) => ({ ...l, centers: false })));
  }, [state.selectedAcademy]);

  const handleCenterSelect = useCallback((center: CenterListItem) => {
    setState((prev) => ({
      ...prev,
      selectedCenter: center,
      ageCategoryList: [],
      selectedAgeCategory: "",
      batchList: [],
      selectedBatch: null,
      selectedSlot: null,
      selectedDynamicSlots: [],
      selectedFee: null,
      selectedTrainingFrequency: "",
      selectedPaymentFrequency: "",
    }));
  }, []);

  // ── Load age categories and batches ───────────────────────────────────────
  useEffect(() => {
    if (!state.selectedCenter) return;
    setLoading((l) => ({ ...l, ageCategories: true }));
    listAgeCategoriesByCenter(state.selectedCenter._id)
      .then((list) => setState((prev) => ({ ...prev, ageCategoryList: list })))
      .catch(() => setState((prev) => ({ ...prev, ageCategoryList: [] })))
      .finally(() => setLoading((l) => ({ ...l, ageCategories: false })));

    setLoading((l) => ({ ...l, batches: true }));
    listAdminBatches(state.selectedCenter._id)
      .then((list) => setState((prev) => ({ ...prev, batchList: list })))
      .catch(() => setState((prev) => ({ ...prev, batchList: [] })))
      .finally(() => setLoading((l) => ({ ...l, batches: false })));
  }, [state.selectedCenter]);

  const handleAgeCategorySelect = useCallback((ageCategory: string) => {
    setState((prev) => ({
      ...prev,
      selectedAgeCategory: ageCategory,
      selectedBatch: null,
      selectedSlot: null,
      selectedDynamicSlots: [],
      selectedFee: null,
      selectedTrainingFrequency: "",
      selectedPaymentFrequency: "",
    }));
  }, []);

  const handleBatchSelect = useCallback((batch: BatchListItem) => {
    setState((prev) => ({
      ...prev,
      selectedBatch: batch,
      selectedSlot: null,
      selectedDynamicSlots: [],
      selectedFee: null,
      selectedTrainingFrequency: "",
      selectedPaymentFrequency: "",
    }));
  }, []);

  // ── Slot selects ──────────────────────────────────────────────────────────
  const handleSlotSelect = useCallback((slot: SelectedSlot) => {
    setState((prev) => ({ ...prev, selectedSlot: slot }));
  }, []);

  const handleDynamicSlotToggle = useCallback((slot: SelectedSlot) => {
    setState((prev) => {
      const exists = prev.selectedDynamicSlots.some(
        (s) =>
          s.day === slot.day && s.startTime === slot.startTime && s.endTime === slot.endTime
      );
      return {
        ...prev,
        selectedDynamicSlots: exists
          ? prev.selectedDynamicSlots.filter(
              (s) =>
                !(
                  s.day === slot.day &&
                  s.startTime === slot.startTime &&
                  s.endTime === slot.endTime
                )
            )
          : [...prev.selectedDynamicSlots, slot],
      };
    });
  }, []);

  // ── Frequencies ───────────────────────────────────────────────────────────
  const handleTrainingFrequencySelect = useCallback(
    (freq: string) => {
      const fee =
        state.selectedBatch?.fees.find((f) => f.trainingFrequency === freq) ?? null;
      setState((prev) => ({
        ...prev,
        selectedTrainingFrequency: freq,
        selectedFee: fee,
        selectedPaymentFrequency: "",
      }));
    },
    [state.selectedBatch]
  );

  const handlePaymentFrequencySelect = useCallback((freq: string) => {
    setState((prev) => ({ ...prev, selectedPaymentFrequency: freq }));
  }, []);

  // ── Schedule & payment setters ────────────────────────────────────────────
  const handleStartDateChange = (v: string) =>
    setState((p) => ({
      ...p,
      startDate: v,
      endDate: p.endDate && p.endDate <= v ? "" : p.endDate,
    }));
  const handleEndDateChange = (v: string) =>
    setState((p) => ({ ...p, endDate: v }));
  const handlePaidAtChange = (v: string) =>
    setState((p) => ({ ...p, paidAt: v }));
  const handlePaymentAmountChange = (v: string) =>
    setState((p) => ({ ...p, paymentAmount: v }));

  // ── Validation ────────────────────────────────────────────────────────────
  const validateForSubmit = (): string | null => {
    // Player is required when creating; in edit mode the backend keeps the existing value if omitted
    if (mode === "add") {
      if (state.playerMode === "phone") {
        if (!state.selectedPlayer) return "Please select a player.";
      } else {
        if (!state.directPlayerCreated)
          return "Please create a player profile before submitting.";
      }
    }

    if (!state.selectedAcademy) return "Please select an academy.";
    if (!state.selectedSport) return "Please select a sport.";
    if (!state.selectedCenter) return "Please select a center.";
    // Batch/age category stay optional for Offline — only Excel/Online require them
    if (
      state.selectedBatch &&
      state.selectedAgeCategory &&
      state.selectedBatch.ageCategory !== state.selectedAgeCategory
    )
      return "Selected batch does not belong to the selected age category.";
    if (!state.startDate) return "Please enter the start date.";
    if (!state.endDate) return "Please enter the end date.";
    if (state.endDate <= state.startDate) return "End date must be after start date.";
    if (!state.paidAt) return "Please enter the paid-at date.";

    const amt = parseFloat(state.paymentAmount);
    if (!state.paymentAmount || isNaN(amt) || amt <= 0)
      return "Please enter a valid payment amount.";

    return null;
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = useCallback(async (): Promise<boolean> => {
    const validationError = validateForSubmit();
    if (validationError) {
      setSubmitError(validationError);
      return false;
    }

    setLoading((l) => ({ ...l, submit: true }));
    setSubmitError(null);

    try {
      const s = state;
      const batch = s.selectedBatch;

      const rawUserId =
        s.playerMode === "phone"
          ? (s.searchedProfileData?.user ?? null)
          : (s.directPlayerCreated?.user ?? null);

      // Treat empty string (hydrated from a null user) the same as null
      const userId = rawUserId || null;

      const rawPlayerId =
        s.playerMode === "phone"
          ? (s.selectedPlayer?._id ?? null)
          : (s.directPlayerCreated?.player._id ?? null);

      const playerId = rawPlayerId || null;

      const buildBatchFields = (
        target: CreateOfflineEnrolmentPayload | UpdateOfflineEnrolmentPayload,
        includeDerivedFields: boolean
      ) => {
        if (!batch) return;
        const slotType = batch.slotType as "fixed" | "dynamic";
        target.batch = batch._id;
        if (includeDerivedFields) {
          target.batchName = batch.batchName;
          target.course = batch.course;
        }
        // Batch is the source of truth for ageCategory now that selecting it isn't mandatory
        target.ageCategory = s.selectedAgeCategory || batch.ageCategory;
        target.slotType = slotType;
        if (s.selectedTrainingFrequency) target.trainingFrequency = s.selectedTrainingFrequency;
        if (s.selectedPaymentFrequency) target.paymentFrequency = s.selectedPaymentFrequency;
        if (slotType === "fixed" && s.selectedSlot) {
          target.fixedSlots = [
            {
              days: s.selectedSlot.days ?? [],
              startTime: s.selectedSlot.startTime,
              endTime: s.selectedSlot.endTime,
            },
          ];
        } else if (slotType === "dynamic" && s.selectedDynamicSlots.length > 0) {
          target.slots = s.selectedDynamicSlots.map((sl) => ({
            day: sl.day ?? "",
            startTime: sl.startTime,
            endTime: sl.endTime,
          }));
        }
      };

      if (mode === "edit" && initialEnrolment) {
        const payload: UpdateOfflineEnrolmentPayload = {
          // Only send user/player when we have a valid ID — omitting lets the backend keep its existing value
          ...(userId && { user: userId }),
          ...(playerId && { player: playerId }),
          academy: s.selectedAcademy!._id,
          coachingCenter: s.selectedCenter!._id,
          sports: s.selectedSport,
          startDate: s.startDate,
          endDate: s.endDate,
          paidAt: s.paidAt,
          paymentAmount: parseFloat(s.paymentAmount),
        };
        buildBatchFields(payload, false);
        await updateOfflineEnrolment(initialEnrolment._id, payload);
      } else {
        const payload: CreateOfflineEnrolmentPayload = {
          enrolledThrough: "Offline",
          user: userId,
          player: playerId,
          academy: s.selectedAcademy!._id,
          coachingCenter: s.selectedCenter!._id,
          sports: s.selectedSport,
          location: s.selectedCenter!.basicDetails.location,
          startDate: s.startDate,
          endDate: s.endDate,
          paidAt: s.paidAt,
          paymentAmount: parseFloat(s.paymentAmount),
        };
        buildBatchFields(payload, true);
        await addOfflineEnrolment(payload);
      }

      onSuccess?.();
      return true;
    } catch (err: unknown) {
      const anyErr = err as any;
      const body = anyErr?.response?.data;
      const msg =
        body?.error ||
        body?.message ||
        (typeof err === "string" ? err : null) ||
        "Failed to submit offline enrolment. Please try again.";
      setSubmitError(msg);
      return false;
    } finally {
      setLoading((l) => ({ ...l, submit: false }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, state, initialEnrolment, onSuccess]);

  // ── Derived helpers ───────────────────────────────────────────────────────
  const availablePaymentFrequencies = state.selectedFee
    ? sortAvailablePaymentFrequencies(
        (Object.entries(state.selectedFee.feeInfo) as [string, number | null][])
          .filter(([, v]) => v !== null)
          .map(([k]) => k),
        planFields
      )
    : [];

  const filteredBatchList = state.selectedAgeCategory
    ? state.batchList.filter((batch) => batch.ageCategory === state.selectedAgeCategory)
    : state.batchList;

  const filteredPlayers =
    state.searchedProfileData && state.registerBy
      ? state.searchedProfileData.players.filter((p) =>
          state.registerBy === "self"
            ? p.relationshipType === "self"
            : p.relationshipType === "parent"
        )
      : [];

  const hasPlayer =
    state.playerMode === "phone"
      ? !!state.selectedPlayer
      : !!state.directPlayerCreated;

  return {
    state,
    loading,
    submitError,
    setSubmitError,
    filteredPlayers,
    filteredBatchList,
    availablePaymentFrequencies,
    planFields,
    hasPlayer,
    allowPlayerAttach,
    handlers: {
      handlePlayerModeChange,
      setPhoneNumber,
      handlePhoneSearch,
      handleCreateWebsiteUser,
      setShowAddProfileForm,
      handleAddProfile,
      setNewParentName,
      setNewParentGender,
      setNewParentDob,
      setNewPlayerName,
      setNewPlayerGender,
      setNewPlayerDob,
      handleRegisterByChange,
      handlePlayerSelect,
      setDirectPlayerField,
      handleCreateDirectPlayer,
      handleResetDirectPlayer,
      handleAcademySelect,
      handleSportSelect,
      handleCenterSelect,
      handleAgeCategorySelect,
      handleBatchSelect,
      handleSlotSelect,
      handleDynamicSlotToggle,
      handleTrainingFrequencySelect,
      handlePaymentFrequencySelect,
      handleStartDateChange,
      handleEndDateChange,
      handlePaidAtChange,
      handlePaymentAmountChange,
      handleSubmit,
    },
  };
}
